// Scroll manager (QA row 39). Forward navigations start at the top, but
// back/forward (POP) restores the position the user left that entry at —
// content often arrives async, so restoration retries briefly until the page
// is tall enough. Scrolls are forced instant here: RevampShell sets
// html { scroll-behavior: smooth } and watching the page glide from the
// footer to the top on every route change is exactly the glitch QA flagged.
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

const instantScroll = (top) => {
  const el = document.documentElement;
  const prev = el.style.scrollBehavior;
  el.style.scrollBehavior = "auto";
  window.scrollTo(0, top);
  el.style.scrollBehavior = prev;
};

// Try to land on `y` while late-loading content is still growing the page.
const restore = (y) => {
  const attempt = (triesLeft) => {
    instantScroll(y);
    if (triesLeft <= 0 || Math.abs(window.pageYOffset - y) < 2) return;
    setTimeout(() => attempt(triesLeft - 1), 250);
  };
  requestAnimationFrame(() => attempt(5));
};

const scrollToAnchor = (hash) => {
  const attempt = (triesLeft) => {
    let el = null;
    try {
      el = document.querySelector(hash);
    } catch (e) {
      /* invalid selector in hash — fall through to top */
    }
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (triesLeft > 0) setTimeout(() => attempt(triesLeft - 1), 200);
    else instantScroll(0);
  };
  requestAnimationFrame(() => attempt(5));
};

export default () => {
  const history = useHistory();

  useEffect(() => {
    const positions = new Map(); // location.key -> scrollY
    let currentKey = history.location.key || "root";
    let currentPath = history.location.pathname;

    const unlisten = history.listen((location, action) => {
      // history.listen fires before the new page renders, so this is still
      // the departing page's offset.
      positions.set(currentKey, window.pageYOffset);
      currentKey = location.key || "root";
      const samePath = location.pathname === currentPath;
      currentPath = location.pathname;

      if (action === "POP" && positions.has(currentKey)) {
        restore(positions.get(currentKey));
        return;
      }
      if (location.hash) {
        scrollToAnchor(location.hash);
        return;
      }
      // Query-only navigations (mentor filters, pagination, search — push OR
      // back/forward between filter states) keep the user's place; the pages
      // manage their own result-grid scrolling.
      if (samePath) return;
      instantScroll(0);
    });
    return unlisten;
  }, [history]);

  return null;
};
